import React from "react";
import { Container, Col, Row } from "react-bootstrap";
import { BackNavRow, PageContainer } from "../common/CommonStyles";
import { useTranslation } from "react-i18next";
import {
  AnalyticsContext,
  ViewTeacherResourcesEvent,
} from "../common/context/AnalyticsContext";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBackpack,
  faEnvelope,
  faIdBadge,
  faPaperPlane,
  faPenToSquare,
} from "@fortawesome/pro-regular-svg-icons";
import { Account, AccountContext } from "../common/context/AccountContext";
import { ApiContext } from "../common/context/ApiContext";
import { isNonStudentSchoolUser, isUserClever } from "../common/api";
import { useLiveData } from "../common/utils";
import { TabEnum, TabsContext } from "../common/context/TabsContext";

export const TeacherResourcesPage: React.FC = () => {
  const { t } = useTranslation();
  const { logEvent } = React.useContext(AnalyticsContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const { getTeacherResources } = React.useContext(ApiContext);
  const { setCurrentTab } = React.useContext(TabsContext);
  const [publicSiteBaseUrl, setPublicSiteBaseUrl] = React.useState("");
  const [userCanAccessTeacherResources, setUserCanAccessTeacherResources] =
    React.useState<boolean>();
  const [userIsClever, setUserIsClever] = React.useState<boolean>();
  const [userIsStaff, setUserIsStaff] = React.useState<boolean>();
  const [emailFromTeacherToParentsLink, setEmailFromTeacherToParentsLink] =
    React.useState("/email-from-teacher-to-parents/");

  useLiveData(
    React.useCallback((account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setUserCanAccessTeacherResources(
          isNonStudentSchoolUser(account.user) ||
            account.user.is_staff ||
            account.user.id === 518674 || // Sheynelle
            account.user.id === 519227 || // Sean
            account.user.id === 11 // Andrea
        );
        setUserIsClever(isUserClever(account.user));
        setUserIsStaff(
          account.user.is_staff ||
          account.user.id === 518674 || // Sheynelle
          account.user.id === 519227 || // Sean
          account.user.id === 11 // Andrea
        );
      }
    }, []),
    accountLiveData
  );

  React.useEffect(() => {
    if (!userCanAccessTeacherResources) {
      return;
    }
    getTeacherResources().then((data) => {
      if (data?.email_from_teacher_to_parents_path) {
        setEmailFromTeacherToParentsLink(
          data.email_from_teacher_to_parents_path
        );
      }
    });
  }, [userCanAccessTeacherResources, getTeacherResources]);

  React.useEffect(() => {
    logEvent(new ViewTeacherResourcesEvent());
    setPublicSiteBaseUrl(
      window.location.origin.replace(
        /^https:\/\/(.*)superteacherapp.com/,
        "https://$1getsuperteacher.com"
      )
    );
    setCurrentTab(TabEnum.TeacherResources);
  }, [logEvent, setPublicSiteBaseUrl, setCurrentTab]);

  return (
    <PageContainer>
      <BackNavRow label={t("generic_nav_back")}>
        <h2>{t("teacher_resources_title")}</h2>
      </BackNavRow>
      {userCanAccessTeacherResources && (
        <Container>
          <Row className="justify-content-center mt-4">
            <Col xs={12} lg={8} xl={6}>
              <Container className="mt-2 mb-4">
                <div className="border rounded overflow-hidden">
                  <a
                    href="https://drive.google.com/drive/folders/1V7rLCK-Q4D9fm1I3lxh-O63AAtbH09mv"
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Row
                      className="border-bottom"
                      style={{
                        marginLeft: 0,
                        marginRight: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        marginInline: "0.5rem",
                      }}
                    >
                      <Col
                        xs="auto"
                        className="py-2"
                        style={{
                          borderLeft: "none",
                          borderRight: "none",
                          width: "2.5rem",
                          textAlign: "center",
                        }}
                      >
                        <FontAwesomeIcon icon={faBackpack} />
                      </Col>
                      <Col
                        className="py-2 px-2"
                        style={{
                          flexGrow: 1,
                          borderLeft: "none",
                          borderRight: "none",
                        }}
                      >
                        {t("teacher_resources_parent_flyers")}
                      </Col>
                    </Row>
                  </a>

                  <a
                    href={publicSiteBaseUrl + emailFromTeacherToParentsLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Row
                      className="border-bottom"
                      style={{
                        marginLeft: 0,
                        marginRight: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        borderBottom: undefined,
                        marginInline: "0.5rem",
                      }}
                    >
                      <Col
                        xs="auto"
                        className="py-2"
                        style={{
                          borderLeft: "none",
                          borderRight: "none",
                          width: "2.5rem",
                          textAlign: "center",
                        }}
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                      </Col>
                      <Col
                        className="py-2 px-2"
                        style={{
                          flexGrow: 1,
                          borderLeft: "none",
                          borderRight: "none",
                        }}
                      >
                        {t("teacher_resources_parent_emails")}
                      </Col>
                    </Row>
                  </a>

                  {(userIsClever || userIsStaff) && (
                    <a
                      href="https://support.clever.com/hc/s/articles/360000127406?language=en_US"
                      rel="noopener noreferrer"
                      target="_blank"
                      style={{
                        display: "block",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <Row
                        className="border-bottom"
                        style={{
                          marginLeft: 0,
                          marginRight: 0,
                          paddingLeft: 0,
                          paddingRight: 0,
                          borderBottom: undefined,
                          marginInline: "0.5rem",
                        }}
                      >
                        <Col
                          xs="auto"
                          className="py-2"
                          style={{
                            borderLeft: "none",
                            borderRight: "none",
                            width: "2.5rem",
                            textAlign: "center",
                          }}
                        >
                          <FontAwesomeIcon icon={faIdBadge} />
                        </Col>
                        <Col
                          className="py-2 px-2"
                          style={{
                            flexGrow: 1,
                            borderLeft: "none",
                            borderRight: "none",
                          }}
                        >
                          {t("teacher_resources_clever_badges")}
                        </Col>
                      </Row>
                    </a>
                  )}

                  <Link
                    to={`/feedback/`}
                    style={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Row
                      className="border-bottom"
                      style={{
                        marginLeft: 0,
                        marginRight: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        borderBottom: undefined,
                        marginInline: "0.5rem",
                      }}
                    >
                      <Col
                        xs="auto"
                        className="py-2"
                        style={{
                          borderLeft: "none",
                          borderRight: "none",
                          width: "2.5rem",
                          textAlign: "center",
                        }}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                      </Col>
                      <Col
                        className="py-2 px-2"
                        style={{
                          flexGrow: 1,
                          borderLeft: "none",
                          borderRight: "none",
                        }}
                      >
                        {t("send_feedback")}
                      </Col>
                    </Row>
                  </Link>
                  <Link
                    to={`/feedback/?prefill=1`}
                    style={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Row
                      style={{
                        marginLeft: 0,
                        marginRight: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        borderBottom: undefined,
                        marginInline: "0.5rem",
                      }}
                    >
                      <Col
                        xs="auto"
                        className="py-2"
                        style={{
                          borderLeft: "none",
                          borderRight: "none",
                          width: "2.5rem",
                          textAlign: "center",
                        }}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Col>
                      <Col
                        className="py-2 px-2"
                        style={{
                          flexGrow: 1,
                          borderLeft: "none",
                          borderRight: "none",
                        }}
                      >
                        {t("request_content")}
                      </Col>
                    </Row>
                  </Link>
                </div>
              </Container>
            </Col>
          </Row>
        </Container>
      )}
    </PageContainer>
  );
};
